import * as React from 'react';
import { useEffect } from 'react';
import { CenterFormWidget } from '../../widgets/layout/CenterFormWidget';
import { ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { FormLayoutSection, FormLayoutWidget } from '../../widgets/layout/FormLayoutWidget';
import { CheckboxLabelWidget } from '../../widgets/checkbox/CheckboxLabelWidget';
import { styled } from '../../theme';
import { FormikContext, useFormik } from 'formik';
import { SignupPayload } from './SignupPage';
import { Keys, useKeyPress } from '../../hooks/useKeyPress';
import {
  DEFAULT_MIN_JA_LENGTH_PASSWORD,
  DEFAULT_MIN_LENGTH_PASSWORD,
  MAX_INPUT_FIELD_LENGTH,
  validateLength,
  validatePassword
} from '../../utils/Validators';
import { SignupActionSource } from '@journeyapps-platform/types-flight-log';
import { useFlightLogger } from '../../hooks/useFlightLogger';
import { FormikInputWidget } from '../../widgets/inputs/FormikInput';
import { FlightLogInput } from '../../widgets/inputs/FlightLogInput';
import { isJourneyEmail, ProductConfig } from '@journeyapps-platform/types-accounts';
import { ProductContext } from '../../widgets/product/context';
import { GetSignupUserResponse } from '@journeyapps-platform/types-accounts-hub';
import * as _ from 'lodash';

export interface SignupStep1WidgetProps {
  payload: Partial<SignupPayload>;
  user: GetSignupUserResponse;
  next: (payload: Partial<SignupPayload>) => Promise<any>;
}

interface SignupStep1Values {
  first_name: string;
  last_name: string;
  password: string;
  password_verify: string;
  org_name: string;
}

enum FieldLabel {
  FIRST_NAME = 'First name',
  LAST_NAME = 'Last name',
  ORG_NAME = 'Organization name'
}

export const SignupStep1Widget: React.FC<SignupStep1WidgetProps> = (props) => {
  const flightLogger = useFlightLogger({ stepId: 'signup-step1', userId: props.user.id });
  const [accept, setAccept] = React.useState(!!props.payload.acceptTerms);
  const [checkedError, setCheckedError] = React.useState('');
  const [checkedFocussed, setCheckedFocussed] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      first_name: props.payload.firstName || '',
      last_name: props.payload.lastName || '',
      org_name: props.payload.orgName || '',
      password: props.payload.password || '',
      password_verify: props.payload.password || ''
    },
    onSubmit: async (event) => {
      if (!accept) {
        setCheckedError('You need to accept the privacy policy and terms of service in order to continue');
        flightLogger.logAction({
          source: SignupActionSource.SYSTEM,
          action_id: 'not-accept-t-c'
        });
        return;
      }
      await props.next({
        firstName: event.first_name,
        lastName: event.last_name,
        password: event.password,
        orgName: event.org_name,
        acceptTerms: accept
      });
    },
    validate: (values) => {
      const errors: Partial<SignupStep1Values> = {};

      if (!values.first_name) {
        errors['first_name'] = 'This is a required field';
      }
      if (!values.last_name) {
        errors['last_name'] = 'This is a required field';
      }
      if (props.user.new_organization && !values.org_name) {
        errors['org_name'] = 'This is a required field';
      }

      if (_.size(errors) > 0) {
        return errors;
      }

      if (props.user.new_organization && !validateLength(values.last_name)) {
        errors['org_name'] = `${FieldLabel.ORG_NAME} must be less than ${MAX_INPUT_FIELD_LENGTH} characters`;
      }
      if (!validateLength(values.first_name)) {
        errors['first_name'] = `${FieldLabel.FIRST_NAME} must be less than ${MAX_INPUT_FIELD_LENGTH} characters`;
      }
      if (!validateLength(values.last_name)) {
        errors['last_name'] = `${FieldLabel.LAST_NAME} must be less than ${MAX_INPUT_FIELD_LENGTH} characters`;
      }

      const testResult = validatePassword(values.password, {
        minLength: isJourneyEmail(props.user.email || '') ? DEFAULT_MIN_JA_LENGTH_PASSWORD : DEFAULT_MIN_LENGTH_PASSWORD
      });
      if (testResult.requiredTestErrors.length > 0) {
        errors['password'] = testResult.requiredTestErrors[0];
      }
      if (values.password !== values.password_verify) {
        errors['password_verify'] = `Passwords don't match`;
      }
      return errors;
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      flightLogger.logAction({ source: SignupActionSource.KEYPRESS, action_id: 'submit-via-enter' });
      formik.submitForm();
    }
  });

  const spacebarPressed = useKeyPress(Keys.SPACE);
  useEffect(() => {
    if (spacebarPressed && checkedFocussed) {
      flightLogger.logAction({ source: SignupActionSource.KEYPRESS, action_id: 'agree-via-space' });
      setAccept(!accept);
    }
  }, [spacebarPressed, checkedFocussed]);

  const getUserSection = (): FormLayoutSection => {
    return {
      key: 'details',
      elements: [
        <FlightLogInput name={'first_name'}>
          <S.StyledInput
            name="first_name"
            onChange={(value) => {
              if (props.user.new_organization && value && !formik.touched.org_name) {
                formik.setFieldValue('org_name', `${value}'s Projects`);
              }
            }}
            input={{
              placeholder: FieldLabel.FIRST_NAME,
              onBlur: (e) => {
                const value = e.target?.value;
                if (value) {
                  flightLogger.logAction({
                    source: SignupActionSource.INPUT,
                    action_id: 'first_name'
                  });
                }
                formik.handleBlur(e);
              }
            }}
          />
        </FlightLogInput>,
        <FlightLogInput name={'last_name'}>
          <S.StyledInput
            name="last_name"
            input={{
              placeholder: FieldLabel.LAST_NAME,
              onBlur: (e) => {
                const value = e.target?.value;
                if (value) {
                  flightLogger.logAction({
                    source: SignupActionSource.INPUT,
                    action_id: 'last_name'
                  });
                }
                formik.handleBlur(e);
              }
            }}
          />
        </FlightLogInput>,
        props.user.new_organization ? (
          <FlightLogInput name={'org_name'}>
            <S.StyledInput
              name="org_name"
              input={{
                placeholder: FieldLabel.ORG_NAME,
                onBlur: (e) => {
                  const value = e.target?.value;
                  if (value) {
                    flightLogger.logAction({
                      source: SignupActionSource.INPUT,
                      action_id: 'org_name'
                    });
                  }
                  formik.handleBlur(e);
                }
              }}
            />
          </FlightLogInput>
        ) : null
      ].filter((e) => !!e)
    };
  };

  const getPasswordSection = (): FormLayoutSection => {
    return {
      key: 'password',
      elements: [
        <S.StyledInput
          name="password"
          input={{
            type: 'password',
            placeholder: 'Password'
          }}
        />,
        <FlightLogInput name={'password_verify'}>
          <S.StyledInput
            name="password_verify"
            input={{
              type: 'password',
              placeholder: 'Confirm password'
            }}
          />
        </FlightLogInput>
      ]
    };
  };

  const getTnCsSection = (config: ProductConfig): FormLayoutSection => {
    const { privacy_policy, terms_of_service_url } = config;
    return {
      key: 'submit',
      elements: [
        <CheckboxLabelWidget
          label={
            <S.Label>
              I accept the{' '}
              <S.Link
                onClick={(e) => {
                  e.stopPropagation();
                  flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'privacy-policy-link' });
                }}
                href={privacy_policy}
                target="_blank"
              >
                Privacy Policy{' '}
              </S.Link>
              and{' '}
              <S.Link
                onClick={(e) => {
                  e.stopPropagation();
                  flightLogger.logAction({
                    source: SignupActionSource.CLICK,
                    action_id: 'terms-and-conditions-link'
                  });
                }}
                href={terms_of_service_url}
                target="_blank"
              >
                Terms of Service
              </S.Link>
            </S.Label>
          }
          checked={accept}
          error={checkedError}
          onChange={(checked) => {
            flightLogger.logAction({
              source: SignupActionSource.INPUT,
              action_id: 'agree-t-cs'
            });
            setCheckedError('');
            setAccept(checked);
          }}
          onFocus={() => {
            setCheckedFocussed(true);
          }}
          onBlur={() => {
            setCheckedFocussed(false);
          }}
        />,
        <ButtonWidget
          label="Next"
          disabled={!accept || !formik.isValid}
          action={async (event, loading) => {
            loading(true);
            flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'submit-button' });
            await formik.submitForm();
            loading(false);
          }}
        />
      ]
    };
  };

  return (
    <ProductContext.Consumer>
      {(product) => {
        return (
          <FormikContext.Provider value={formik}>
            <CenterFormWidget primaryLabel="Finish Sign-Up" secondaryLabel={product.signup_secondary_label || ''}>
              <FormLayoutWidget sections={[getUserSection(), getPasswordSection(), getTnCsSection(product)]} />
            </CenterFormWidget>
          </FormikContext.Provider>
        );
      }}
    </ProductContext.Consumer>
  );
};

namespace S {
  export const Link = styled.a`
    text-decoration: none;
    font-weight: bold;
    color: ${(p) => p.theme.text.primary};
    white-space: nowrap;
  `;

  export const Label = styled.div`
    max-width: 300px;
    padding-left: 10px;
    font-size: 16px;
  `;

  export const StyledInput = styled(FormikInputWidget)`
    max-width: 300px;
  `;
}
